import { CustomPropertyType, ReviewConfig } from "generated/graphql";
import Flex from "components/Flex";
import { Box, Button } from "@mui/material";
import { FormState, schemas, useForm } from "helpers/FormHelpers";
import { CLUSTER_TYPES, displayDateString, DISTINCT_MODES, PAGE_TYPES } from "../../../Backend";
import { P } from "theme/typography";
import { useAppSelector } from "redux/hooks";
import { selectDocumentProperties, selectPageLabels } from "redux/slices/lensEditor/lensEditor.query";
import {
  FormCheckbox,
  FormCustomProperties,
  FormMultiSelectField,
  FormSelectField,
  FormTextField,
} from "helpers/FormHelpersComponents";

export interface ReviewConfigFormProps {
  reviewConfig: ReviewConfig;
  onCancel: () => void;
  onSave: (reviewConfig: ReviewConfig) => void;
}

export type ReviewConfigField =
  | "name"
  | "medicationReview"
  | "documentReview"
  | "suggestedPageTypes"
  | "excludedPageTypes"
  | "suggestedClusterTypes"
  | "suggestedLabelNames"
  | "excludedLabelNames"
  | "showCancer"
  | "showStage"
  | "showFamily"
  | "distinctMode"
  | "hideNegated"
  | "excludedClusterTypes"
  | "requiredCustomProperties"
  | "excludedCustomProperties";

const schema = {
  name: schemas.nonEmpty("Name"),
  medicationReview: schemas.nonEmpty("Medication Review"),
  documentReview: schemas.nonEmpty("Document Review"),
  suggestedPageTypes: schemas.optionalArray,
  excludedPageTypes: schemas.optionalArray,
  suggestedClusterTypes: schemas.optionalArray,
  suggestedLabelNames: schemas.optionalArray,
  excludedLabelNames: schemas.optionalArray,
  showCancer: schemas.nonEmpty("Cancer Features"),
  showStage: schemas.nonEmpty("Stage Features"),
  showFamily: schemas.nonEmpty("Family Findings"),
  distinctMode: schemas.customType(true),
  hideNegated: schemas.nonEmpty("Hide Negated"),
  excludedClusterTypes: schemas.optionalArray,
  requiredCustomProperties: schemas.optionalArray,
  excludedCustomProperties: schemas.optionalArray,
};

const ReviewConfigForm = (props: ReviewConfigFormProps) => {
  const { reviewConfig, onCancel, onSave } = props;
  const pageLabels = useAppSelector(selectPageLabels);
  const documentProperties = useAppSelector(selectDocumentProperties);

  const initial: FormState<ReviewConfigField> = {
    isValid: false,
    values: {
      name: reviewConfig.name,
      medicationReview: reviewConfig.medicationReview,
      documentReview: reviewConfig.documentReview,
      suggestedPageTypes: reviewConfig.suggestedPageTypes,
      excludedPageTypes: reviewConfig.excludedPageTypes,
      suggestedClusterTypes: reviewConfig.suggestedClusterTypes,
      suggestedLabelNames: reviewConfig.suggestedLabelNames,
      excludedLabelNames: reviewConfig.excludedLabelNames,
      showCancer: reviewConfig.showCancer,
      showStage: reviewConfig.showStage,
      showFamily: reviewConfig.showFamily,
      distinctMode: reviewConfig.distinctMode,
      hideNegated: reviewConfig.hideNegated,
      excludedClusterTypes: reviewConfig.excludedClusterTypes,
      requiredCustomProperties: reviewConfig.requiredCustomProperties,
      excludedCustomProperties: reviewConfig.excludedCustomProperties,
    },
    touched: {
      name: false,
      medicationReview: false,
      documentReview: false,
      suggestedPageTypes: false,
      excludedPageTypes: false,
      suggestedClusterTypes: false,
      suggestedLabelNames: false,
      excludedLabelNames: false,
      showCancer: false,
      showStage: false,
      showFamily: false,
      distinctMode: false,
      hideNegated: false,
      excludedClusterTypes: false,
      requiredCustomProperties: false,
      excludedCustomProperties: false,
    },
    errors: {
      name: [],
      medicationReview: [],
      documentReview: [],
      suggestedPageTypes: [],
      excludedPageTypes: [],
      suggestedClusterTypes: [],
      suggestedLabelNames: [],
      excludedLabelNames: [],
      showCancer: [],
      showStage: [],
      showFamily: [],
      distinctMode: [],
      hideNegated: [],
      excludedClusterTypes: [],
      requiredCustomProperties: [],
      excludedCustomProperties: [],
    },
  };

  const form = useForm<ReviewConfigField>(initial, schema);

  const handleSave = () => {
    const values = form.formStateHandle[0].values;
    const configToSave: ReviewConfig = {
      ...reviewConfig,
      name: values["name"],
      medicationReview: values["medicationReview"],
      documentReview: values["documentReview"],
      suggestedPageTypes: values["suggestedPageTypes"],
      excludedPageTypes: values["excludedPageTypes"],
      suggestedClusterTypes: values["suggestedClusterTypes"],
      suggestedLabelNames: values["suggestedLabelNames"],
      excludedLabelNames: values["excludedLabelNames"],
      showCancer: values["showCancer"],
      showStage: values["showStage"],
      showFamily: values["showFamily"],
      distinctMode: values["distinctMode"],
      hideNegated: values["hideNegated"],
      excludedClusterTypes: values["excludedClusterTypes"],
      requiredCustomProperties: values["requiredCustomProperties"],
      excludedCustomProperties: values["excludedCustomProperties"],
    };
    onSave(configToSave);
  };

  return (
    <Box display="grid" gap={1} m={2} width="400px">
      <P>ID: {reviewConfig.id}</P>
      <P>Created At: {displayDateString(reviewConfig.createdAt)}</P>
      <FormTextField label="Name" id="name" useForm={form} />
      <FormCheckbox id="documentReview" label="Document Review" useForm={form} />
      <FormCheckbox id="medicationReview" label="Medication Review" useForm={form} />
      <FormCheckbox id="showCancer" useForm={form} label="Show Cancer Filters" />
      <FormCheckbox id="showStage" useForm={form} label="Show Stage Filter" />
      <FormCheckbox id="showFamily" useForm={form} label="Family Findings" />
      <FormCheckbox id="hideNegated" useForm={form} label="Hide Negated Findings" />
      <FormMultiSelectField id="suggestedPageTypes" useForm={form} options={PAGE_TYPES} label="Suggested Page Types" />
      <FormMultiSelectField id="excludedPageTypes" useForm={form} options={PAGE_TYPES} label="Excluded Page Types" />
      <FormMultiSelectField
        id="suggestedClusterTypes"
        useForm={form}
        options={CLUSTER_TYPES}
        label="Suggested Cluster Types"
      />
      <FormMultiSelectField
        id="suggestedLabelNames"
        useForm={form}
        options={pageLabels.map((l) => l.name)}
        label="Suggested Label Names"
      />
      <FormMultiSelectField
        id="excludedLabelNames"
        useForm={form}
        options={pageLabels.map((l) => l.name)}
        label="Excluded Label Names"
      />
      <FormSelectField
        id="distinctMode"
        useForm={form}
        required
        options={DISTINCT_MODES}
        label="Lens Hits Distinct Mode"
      />
      <FormMultiSelectField
        id="excludedClusterTypes"
        useForm={form}
        options={CLUSTER_TYPES}
        label="Excluded Cluster Types"
      />
      <P variant="subtitle3">Required Document Properties</P>
      <FormCustomProperties
        id="requiredCustomProperties"
        useForm={form}
        customProperties={documentProperties.filter((dp) => dp.dataType !== CustomPropertyType.Date)}
      />
      <P variant="subtitle3">Excluded Document Properties</P>
      <FormCustomProperties
        id="excludedCustomProperties"
        useForm={form}
        customProperties={documentProperties.filter((dp) => dp.dataType !== CustomPropertyType.Date)}
      />
      <Flex mt={1} flexDirection="row" justifyContent="flex-end">
        <Button onClick={onCancel} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </Box>
  );
};

export default ReviewConfigForm;
